import React, { createElement, Fragment, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useSearchBox } from 'react-instantsearch-core';
import { autocomplete } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { SearchClient } from 'algoliasearch/lite';
import { styled } from 'styled-components';
import IconSearch from '@images/search.svg';
import Colors from '@components/shared/Colors';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { NAV_MENU_BREAKPOINT } from '@constants';

const ALGOLIA_QUERY_SUGGESTIONS_HITS_PER_PAGE = 6;

type AutocompleteProps = {
  searchClient: SearchClient;
};

function AlgoliaAutocomplete({ searchClient }: AutocompleteProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const { query, refine: setQuery } = useSearchBox();
  const { t } = useTranslation();
  const [instantSearchUiState, setInstantSearchUiState] = useState<{ query: string }>({ query });
  const querySuggestionsPlugin = useMemo(
    () =>
      createQuerySuggestionsPlugin({
        searchClient,
        indexName: `${process.env.ALGOLIA_INDEX_NAME}_query_suggestions`,
        getSearchParams() {
          return { hitsPerPage: ALGOLIA_QUERY_SUGGESTIONS_HITS_PER_PAGE };
        }
      }),
    [searchClient]
  );
  const handleSearch = useCallback((queryString: string) => {
    setInstantSearchUiState({ query: queryString });
  }, []);

  useEffect(() => {
    setQuery(instantSearchUiState.query);
  }, [instantSearchUiState]);

  useEffect(() => {
    if (!containerRef.current) {
      return () => {};
    }

    const autocompleteInstance = autocomplete({
      placeholder: t('components.search'),
      detachedMediaQuery: 'none',
      openOnFocus: true,
      container: containerRef.current,
      initialState: { query },
      plugins: [querySuggestionsPlugin],
      onReset() {
        setInstantSearchUiState({ query: '' });
      },
      onSubmit({ state }) {
        handleSearch(state.query);
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          setInstantSearchUiState({
            query: state.query
          });
        }
      },
      renderer: { createElement, Fragment, render: () => {} },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;

          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }

        panelRootRef.current.render(children);
      }
    });

    const scrollableElement = document.querySelector('.nav-menu-container');

    if (!scrollableElement) {
      return () => {
        autocompleteInstance.destroy();
      };
    }

    const handleScroll = () => {
      autocompleteInstance.setIsOpen(false);
    };

    scrollableElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      autocompleteInstance.destroy();
    };
  }, [querySuggestionsPlugin]);

  return (
    <Wrapper>
      <SearchIconWrapper onClick={() => handleSearch(query)}>
        <IconSearch />
      </SearchIconWrapper>
      <AutocompleteWrapper ref={containerRef} />
    </Wrapper>
  );
}

export default AlgoliaAutocomplete;

const Wrapper = styled.div`
  position: relative;
  width: 420px;
  align-self: center;

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  cursor: pointer;
  z-index: 100;
  fill: ${Colors.navyBlue};
  height: 62px;
  width: 46px;
  display: flex;
  justify-content: center;

  svg {
    align-self: center;
  }
`;

const SearchButtonWrapper = styled.div`
  right: 0;
  position: absolute;
  cursor: pointer;
  z-index: 100;
  fill: ${Colors.navyBlue};
  height: 62px;
  width: 46px;
  display: flex;
  justify-content: center;

  svg {
    align-self: center;
    justify-self: center;
  }
`;

const AutocompleteWrapper = styled.div`
  .aa-Autocomplete {
    width: 100%;
    display: flex;
    align-self: center;
    border-bottom: 2px solid ${Colors.ecru};

    .aa-Form {
      width: 100%;
      background-color: transparent;
      border: none;
      box-shadow: none;
      border-radius: 0px;
    }

    .aa-Input {
      color: ${Colors.navyBlue};
      font-family: BrownStd;
      width: 100%;
      outline: none;
      flex-grow: 1;
      border: none;
      padding: 16px 16px 16px 46px;
      font-size: 14px;
      height: auto;
      line-height: 30px;
      cursor: text;

      &:focus {
        outline: none !important;
      }
    }

    .aa-InputWrapperPrefix {
      order: 5;
    }

    .aa-InputWrapperSuffix {
      order: 6;
    }

    .aa-SubmitButton {
      opacity: 0;
    }
  }
`;
